const DEFAULT_BATCH_SIZE = 40;
const DEFAULT_MAX_BATCH_CHARACTERS = 8000;
const DEFAULT_TIMEOUT_MS = 20000;
const CJK_PATTERN = /[\u3400-\u9fff]/u;

let translateClientPromise = null;

function abortError() {
  const error = new Error('翻译任务已取消');
  error.name = 'AbortError';
  return error;
}

function requestError(xhr) {
  const status = Number(xhr?.status || 0);
  const detail = String(xhr?.responseText || '').trim();
  const error = new Error(status ? `翻译服务请求失败（HTTP ${status}）` : '翻译服务请求失败');
  if (detail) error.detail = detail;
  return error;
}

export async function loadTranslateClient() {
  if (!translateClientPromise) {
    translateClientPromise = import('i18n-jsautotranslate')
      .then((module) => {
        const client = module.default || module;
        if (!client?.request?.translateText || !client?.service?.use) {
          throw new Error('translate.js 未提供文本翻译接口');
        }
        client.selectLanguageTag.show = false;
        client.service.use('client.edge');
        return client;
      })
      .catch((error) => {
        translateClientPromise = null;
        throw error;
      });
  }
  return translateClientPromise;
}

export function requestTranslationBatch(client, texts, { timeoutMs = DEFAULT_TIMEOUT_MS, signal } = {}) {
  if (!Array.isArray(texts) || texts.length === 0) return Promise.resolve([]);
  if (signal?.aborted) return Promise.reject(abortError());

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', handleAbort);
      callback(value);
    };
    const handleAbort = () => finish(reject, abortError());
    const timer = window.setTimeout(() => {
      finish(reject, new Error(`翻译服务 ${timeoutMs}ms 内未响应`));
    }, timeoutMs);
    signal?.addEventListener('abort', handleAbort, { once: true });

    try {
      client.request.translateText(
        {
          from: 'chinese_simplified',
          to: 'english',
          texts,
        },
        (response) => {
          if (
            !response
            || Number(response.result) !== 1
            || !Array.isArray(response.text)
            || response.text.length !== texts.length
          ) {
            finish(reject, new Error('翻译服务返回了无效结果'));
            return;
          }
          finish(resolve, response.text.map((value) => String(value || '').trim()));
        },
        (xhr) => finish(reject, requestError(xhr))
      );
    } catch (error) {
      finish(reject, error);
    }
  });
}

function uniqueSourceTexts(texts) {
  const seen = new Set();
  return (Array.isArray(texts) ? texts : [])
    .map((value) => String(value || '').trim())
    .filter((value) => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
}

function translationBatches(texts, batchSize, maxBatchCharacters) {
  const batches = [];
  let batch = [];
  let characters = 0;
  texts.forEach((text) => {
    if (batch.length > 0 && (batch.length >= batchSize || characters + text.length > maxBatchCharacters)) {
      batches.push(batch);
      batch = [];
      characters = 0;
    }
    batch.push(text);
    characters += text.length;
  });
  if (batch.length > 0) batches.push(batch);
  return batches;
}

async function requestWithRetry(client, batch, options) {
  const retries = Math.max(0, Number(options.retries) || 0);
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await requestTranslationBatch(client, batch, options);
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      lastError = error;
    }
  }
  throw lastError;
}

export async function autoTranslateTexts(texts, options = {}) {
  const sourceTexts = uniqueSourceTexts(texts);
  const translations = new Map();
  const failures = [];
  if (sourceTexts.length === 0) {
    return { translations, failures, total: 0, completed: 0 };
  }

  const client = options.client || await (options.loadClient || loadTranslateClient)();
  const batches = translationBatches(
    sourceTexts,
    Math.max(1, Number(options.batchSize) || DEFAULT_BATCH_SIZE),
    Math.max(1, Number(options.maxBatchCharacters) || DEFAULT_MAX_BATCH_CHARACTERS)
  );
  let completed = 0;

  for (const batch of batches) {
    if (options.signal?.aborted) throw abortError();
    try {
      const translated = await requestWithRetry(client, batch, {
        timeoutMs: options.timeoutMs || DEFAULT_TIMEOUT_MS,
        retries: options.retries ?? 1,
        signal: options.signal,
      });
      batch.forEach((source, index) => {
        const value = translated[index];
        if (value && !CJK_PATTERN.test(value)) translations.set(source, value);
        else failures.push({ source, reason: '翻译结果为空或仍包含中文' });
      });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      batch.forEach((source) => failures.push({ source, reason: error?.message || '翻译失败' }));
    }
    completed += batch.length;
    options.onProgress?.({
      completed,
      total: sourceTexts.length,
      succeeded: translations.size,
      failed: failures.length,
    });
  }

  return { translations, failures, total: sourceTexts.length, completed };
}
